import {CommentsCategory, useAddComment} from '@domain/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useUpmPlant} from '@hconnect/upm'
import {useCallback, useMemo, useRef, useState} from 'react'
import {SubmitHandler} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {MachineSentryActionForm} from '../../components/form'
import {TaskResolvedMandatoryComment} from '../../components/TaskDetails/TaskResolvedMandatoryComment'
import {EditTaskCard} from '../../components/TaskEdit/EditTaskCard'
import {MachineSentryCommentsQueryKeyPrefix, FEATURE_NAMESPACE} from '../../consts'
import {useMachineSentryAction, useUpdateMachineSentryAction} from '../../hooks'
import {mapToMachineSentryAction, mapToMachineSentryActionFormValues} from '../../mappers'
import {
  SideCardModes,
  SideCardItemMode,
  HProduceTask,
  TaskSource,
  HProduceTaskIdentifiers,
  MachineSentryActionFormValues,
  TaskStatus
} from '../../types'

type Props = {
  task: HProduceTaskIdentifiers
  onClose: (original?: HProduceTask, editVersion?: HProduceTask) => void
  onTaskTypeChange: (taskType: TaskSource | undefined) => void
  setMode: (next: SideCardModes) => void
}

export const EditMachineSentryActionContainer = ({
  task,
  onClose,
  onTaskTypeChange,
  setMode
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const upmPlant = useUpmPlant({plantId: task.plantId, shouldRaiseError: false})
  const plantUpmId = upmPlant?.upmId
  const {
    data,
    isLoading: isLoadingMachineSentryAction,
    isError,
    refetch,
    isRefetching
  } = useMachineSentryAction({plantUpmId, externalId: task.externalId})
  const plantTimezone = upmPlant?.timezone
  const {mutateAsync: updateMachineSentryAction} = useUpdateMachineSentryAction()
  const {mutateAsync: addComment} = useAddComment(
    {
      plantId: task.plantId,
      commentsCategory: CommentsCategory.MachineSentryActions
    },
    MachineSentryCommentsQueryKeyPrefix
  )

  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false)
  const [statusComment, setStatusComment] = useState<string | null>(null)
  const resetStatusRef = useRef<(() => void) | null>(null)

  const handleCancel = () => {
    setMode({mode: SideCardItemMode.Details, itemId: task.id})
  }

  const handleStatusChange = useCallback(
    (newValue: TaskStatus | null, resetStatus: () => void) => {
      if (!data) return
      const isChangingToDone = data.status !== newValue && newValue === TaskStatus.Done
      if (isChangingToDone) {
        resetStatusRef.current = resetStatus
        setIsCommentDialogOpen(true)
      } else {
        setStatusComment(null)
      }
    },
    [data]
  )

  const handleCommentDialogClose = useCallback(() => {
    setIsCommentDialogOpen(false)
    resetStatusRef.current?.()
    resetStatusRef.current = null
  }, [])

  const handleCommentSubmit = useCallback((comment: string) => {
    setStatusComment(comment)
    setIsCommentDialogOpen(false)
    resetStatusRef.current = null
  }, [])

  const handleSubmit: SubmitHandler<MachineSentryActionFormValues> = async (formValues) => {
    if (!formValues || !plantUpmId || !data) {
      return
    }

    const dto = mapToMachineSentryAction(formValues, task.externalId, plantUpmId)

    const isStatusChangingToDone =
      data.status !== formValues.status && formValues.status === TaskStatus.Done

    await updateMachineSentryAction(dto, {
      onSuccess: async (updatedMachineSentryAction) => {
        if (isStatusChangingToDone && statusComment) {
          await addComment({
            value: statusComment,
            commentCategoryInstanceId: updatedMachineSentryAction.id
          })
        }
        setMode({mode: SideCardItemMode.Details, itemId: task.id})
      }
    })
  }

  const initialValues = useMemo(() => {
    return data && plantTimezone
      ? mapToMachineSentryActionFormValues(data, plantTimezone)
      : undefined
  }, [data, plantTimezone])

  return (
    <>
      <DataContentWrapper
        data={initialValues}
        showLoader={isLoadingMachineSentryAction}
        isError={isError}
        retryFunction={refetch}
        isRefetching={isRefetching}
        noDataSettings={{
          noDataMessage: t('task.message.taskNotFound'),
          onActionClick: () => onClose()
        }}
      >
        {data && plantTimezone && (
          <EditTaskCard title={data.title}>
            <MachineSentryActionForm
              plantId={task.plantId}
              externalTaskId={task.externalId}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onTaskTypeChange={onTaskTypeChange}
              initialValues={initialValues}
              timezone={plantTimezone}
              onStatusChange={handleStatusChange}
            />
          </EditTaskCard>
        )}
      </DataContentWrapper>
      <TaskResolvedMandatoryComment
        data-test-id="task-resolved-mandatory-comment-modal"
        open={isCommentDialogOpen}
        onClose={handleCommentDialogClose}
        onSubmit={handleCommentSubmit}
      />
    </>
  )
}
